/**
 * Παράθυρο στεγνής δοκιμής (§13.6.2, §13.11).
 *
 * ΓΙΑΤΙ δείχνει ΤΑ ΠΑΝΤΑ: η αποστολή στο efood είναι μη αναστρέψιμη, γυρίζει
 * γυμνό 200 χωρίς αποτέλεσμα ανά γραμμή, και κάθε γραμμή του `POST
 * /catalog/products` ξαναδηλώνει τιμή ΚΑΙ διαθεσιμότητα (§13.6.3). Άρα το μόνο
 * σημείο όπου ένας άνθρωπος μπορεί να σταματήσει ένα λάθος είναι εδώ — με τις
 * ακριβείς κλήσεις, τα ακριβή σώματα και ρητή εξήγηση ποια πεδία ξαναδηλώνονται
 * και γιατί.
 *
 * ΦΑΣΗ 5: η «Αποστολή» ΕΙΝΑΙ ΠΛΕΟΝ ΠΡΑΓΜΑΤΙΚΗ (§13.19.Θ). Ανάβει μόνο όταν
 * ισχύουν ΟΛΑ: ο server έχει άδεια εγγραφής, ο χρήστης είναι διαχειριστής, κάθε
 * εργασία απευθύνεται με ΤΟΥΣ ΔΙΚΟΥΣ ΜΑΣ κωδικούς (`our_sku` — δηλαδή το
 * κατάστημα έχει μεταπτωθεί), κάθε φραγή είναι τσεκαρισμένη και η προεπισκόπηση
 * δεν έχει λήξει. Κάθε 409/403 της πύλης μένει ΜΟΝΙΜΑ στην οθόνη, ποτέ toast.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Coins, FlaskConical, Loader2, Send, ShieldAlert, X,
} from 'lucide-react';
import {
  PlanBlocker, PlanJob, PlanTwin, PriceSyncHeader, RunProgress, SignedPlan, TwinDecisions,
} from '../../services/platformCatalogApi';
import { FIELD_LABELS, fmtCallValue, runStatusLabel } from './catalogUi';

interface Props {
  /**
   * ΜΟΝΟ υπογεγραμμένο σχέδιο: το παράθυρο διαβάζει `plan.run.confirm_hash` και
   * `plan.run.expires_at`. Ο `price-sync` μπορεί να απαντήσει `run: null`
   * («καμία τιμή δεν χρειάζεται αλλαγή») — αυτό ΔΕΝ ανοίγει παράθυρο.
   */
  plan: SignedPlan;
  onClose: () => void;
  /** Ξαναϋπολογίζει το σχέδιο ΜΕ τις αποφάσεις του πάνελ διδύμων. */
  onRerun: (decisions?: TwinDecisions) => void;
  /** Το τελευταίο σχέδιο γύρισε αγνοώντας τις αποφάσεις που στάλθηκαν. */
  twinDecisionsIgnored?: boolean;
  busy: boolean;
  /** org_admin+ (§13.12): μόνο αυτός στέλνει. */
  canPush?: boolean;
  /** Πύλες 1-3 του server (§13.8.1) — `false` = αυτός ο server δεν είναι εκδότης. */
  serverCanPush?: boolean;
  /** Τρέχει η αποστολή αυτή τη στιγμή. */
  pushing?: boolean;
  /** Μόνιμο μήνυμα από 409/403 της τελευταίας αποστολής. */
  pushError?: string | null;
  onPush?: () => void;
  /**
   * Η εκτέλεση που ξεκίνησε ΑΠΟ ΑΥΤΟ το παράθυρο και τρέχει στο παρασκήνιο
   * (202 + ερώτημα κάθε 3΄΄). Όσο υπάρχει, το κουμπί αποστολής μένει κλειστό:
   * δεύτερο πάτημα πάνω στην ίδια εκτέλεση θα έστελνε τα ίδια σώματα ξανά.
   */
  pushProgress?: RunProgress | null;
  /** Ποιο κατάστημα φεύγει πρώτο, με το όνομά του — όχι «#13». */
  canaryStoreName?: string | null;
  /** Η κεφαλίδα του συγχρονισμού τιμών, όταν το σχέδιο ήρθε από εκεί. */
  syncHeader?: PriceSyncHeader | null;
}

/**
 * Κλειδιά ΤΑΥΤΟΤΗΤΑΣ, ποτέ δείκτης πίνακα: μετά από «Νέα δοκιμή» η λίστα φραγών
 * αλλάζει σειρά και μήκος, και μια επιβεβαίωση κλειδωμένη στο «3» θα προσγειωνόταν
 * σε εντελώς άλλη φραγή. Αυτά τα checkbox είναι η ανθρώπινη πύλη πριν από την
 * αποστολή της Φ5 — δεν επιτρέπεται να δείχνουν αλλού.
 */
const blockerKey = (b: PlanBlocker): string =>
  `${b.code}|${b.store_id == null ? '' : b.store_id}|${b.item_code || ''}`;

const twinKeyOf = (t: PlanTwin): string => `${t.store_id}|${t.twin_key}`;

const MAX_BODY_CHARS = 6000;

const prettyBody = (body: unknown): string => {
  if (body === null || body === undefined) return '';
  let text: string;
  try {
    text = JSON.stringify(body, null, 2);
  } catch {
    text = String(body);
  }
  return text.length > MAX_BODY_CHARS ? `${text.slice(0, MAX_BODY_CHARS)}\n… (κομμένο)` : text;
};

const secondsLeft = (expiresAt?: string): number | null => {
  if (!expiresAt) return null;
  const t = new Date(expiresAt).getTime();
  if (!isFinite(t)) return null;
  return Math.round((t - Date.now()) / 1000);
};

const fmtCountdown = (s: number): string => {
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = abs % 60;
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};

const JobAccordion: React.FC<{ job: PlanJob; open: boolean; onToggle: () => void }> = ({ job, open, onToggle }) => (
  <div className="border border-line rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 bg-surface-1 hover:bg-surface-3 text-left"
    >
      {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      <span className="text-sm text-t1 font-medium">#{job.seq} {job.store_name}</span>
      {job.canary && (
        <span className="px-1.5 py-0.5 rounded-lg text-[11px] bg-amber-500/20 text-amber-800 dark:text-amber-200"
          title="Το πρώτο κατάστημα είναι καναρίνι: μέχρι να επαληθευτεί καθαρά, καμία άλλη εργασία δεν ξεκινά">
          καναρίνι
        </span>
      )}
      <span className="ml-auto flex items-center gap-2 text-[11px] text-t3">
        <span title="Πώς αναγνωρίζεται το είδος στο efood">{job.addressing}</span>
        <span>{job.target_env}</span>
        <span title="Ταυτότητα καταστήματος στο efood (hash — ποτέ ο ίδιος ο κωδικός)">
          {job.vendor_id_hash ? job.vendor_id_hash.slice(0, 10) : '—'}
        </span>
        <span>{(job.calls || []).length} κλήσεις</span>
      </span>
    </button>

    {open && (
      <div className="p-3 space-y-3 bg-surface-2">
        {(job.calls || []).map(call => (
          <div key={call.seq} className="border border-line rounded-lg p-2.5">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 rounded bg-slate-500/15 text-t2 font-mono">{call.method}</span>
              <span className="text-t2 font-medium">{call.endpoint}</span>
              {call.chunk && <span className="text-t3">chunk {call.chunk}</span>}
              {call.rows !== undefined && call.rows !== null && <span className="text-t3">{call.rows} γραμμές</span>}
            </div>
            <div className="mt-1 text-[11px] text-t3 break-all font-mono">{call.url}</div>
            {call.note && <div className="mt-1 text-[11px] text-t3 italic">{call.note}</div>}

            {call.changes && call.changes.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {call.changes.map((c, i) => (
                  <li key={`${c.item_code}-${c.field}-${i}`} className="text-xs text-t2">
                    <span className="text-t1">{c.name}</span>{' '}
                    <span className="text-t3">({c.item_code})</span>:{' '}
                    {FIELD_LABELS[c.field] || c.field}{' '}
                    {/* ΣΗΜΑΣΙΟΛΟΓΙΑ ΠΑΡΟΥΣΙΑΣ, ΟΧΙ καλαθιού: στο σχέδιο ο planner
                        γράφει `delete: from=true → to=false` = «υπάρχει» → «διαγράφεται».
                        Με το `fmtEditValue` η γραμμή κατέληγε σε <strong>Να υπάρχει</strong>,
                        δηλαδή το ακριβώς αντίθετο από ό,τι υπογράφει ο χειριστής. */}
                    <span className="text-t3">{fmtCallValue(c.field, c.from)}</span> →{' '}
                    <strong>{fmtCallValue(c.field, c.to)}</strong>
                    {c.via === 'twin' && (
                      <span className="ml-1 px-1 rounded text-[10px] bg-sky-500/20 text-sky-700 dark:text-sky-300">
                        δίδυμο
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {call.echoed && (
              <div className="mt-2 text-[11px] text-t3 bg-surface-1/60 rounded-lg p-2">
                <div className="font-medium text-t2">Πεδία που ξαναδηλώνονται</div>
                <div>{call.echoed.why}</div>
                <ul className="list-disc list-inside">
                  {(call.echoed.fields || []).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}

            {call.body !== undefined && call.body !== null && (
              <pre className="mt-2 text-[11px] text-t2 bg-surface-1/60 rounded-lg p-2 overflow-x-auto">
                {prettyBody(call.body)}
              </pre>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

const DryRunModal: React.FC<Props> = ({
  plan, onClose, onRerun, twinDecisionsIgnored = false, busy,
  canPush = false, serverCanPush = false, pushing = false, pushError = null, onPush,
  pushProgress = null, canaryStoreName = null, syncHeader = null,
}) => {
  const [openJobs, setOpenJobs] = useState<Record<number, boolean>>(() => {
    // Το καναρίνι ανοιχτό εξ ορισμού: είναι η εργασία που κρίνει αν συνεχίζει η
    // εκτέλεση, άρα είναι αυτή που πρέπει να διαβαστεί οπωσδήποτε.
    const first = plan.jobs && plan.jobs.length > 0 ? plan.jobs[0].store_id : null;
    return first === null ? {} : { [first]: true };
  });
  const [ackBlockers, setAckBlockers] = useState<Record<string, boolean>>({});
  const [twinToggles, setTwinToggles] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    (plan.twins || []).forEach(t => { init[twinKeyOf(t)] = !!t.apply_to_all; });
    return init;
  });
  const [left, setLeft] = useState<number | null>(secondsLeft(plan.run.expires_at));

  useEffect(() => {
    if (!plan.run.expires_at) return;
    const id = window.setInterval(() => setLeft(secondsLeft(plan.run.expires_at)), 1000);
    return () => window.clearInterval(id);
  }, [plan.run.expires_at]);

  const blockers = plan.blockers || [];
  const warnings = plan.warnings || [];
  const allAcked = blockers.every(b => !!ackBlockers[blockerKey(b)]);
  const expired = left !== null && left <= 0;

  const twinsChanged = useMemo(
    () => (plan.twins || []).some(t => !!twinToggles[twinKeyOf(t)] !== !!t.apply_to_all),
    [plan.twins, twinToggles]
  );

  /** store_id → twin_key → «μαζί;» — ακριβώς ό,τι δέχεται ο planner. */
  const twinDecisions = useMemo<TwinDecisions>(() => {
    const out: TwinDecisions = {};
    (plan.twins || []).forEach(t => {
      const store = String(t.store_id);
      if (!out[store]) out[store] = {};
      out[store][t.twin_key] = !!twinToggles[twinKeyOf(t)];
    });
    return out;
  }, [plan.twins, twinToggles]);

  /**
   * Ό,τι ΔΕΝ φεύγει. Ο planner τα βγάζει αναλυτικά (`skipped`, `conflicts`) και
   * κρατά προειδοποίηση μόνο όταν αδειάζει ΟΛΟΚΛΗΡΟ κατάστημα — άρα χωρίς αυτή
   * την ενότητα μια χαμένη γραμμή μέσα σε 22 καταστήματα εξαφανίζεται αμίλητη.
   */
  const notSent = useMemo(() => {
    const names: Record<string, string> = {};
    (plan.jobs || []).forEach(j => { names[String(j.store_id)] = j.store_name; });
    const byStore: Record<string, Array<{ kind: 'skip' | 'conflict'; code: string; text: string }>> = {};
    const push = (storeId: unknown, kind: 'skip' | 'conflict', code: string, text: string) => {
      const key = storeId == null ? '' : String(storeId);
      if (!byStore[key]) byStore[key] = [];
      byStore[key].push({ kind, code, text });
    };
    (plan.skipped || []).forEach(s => push(
      s.store_id, 'skip', s.reason,
      s.message || `${s.item_code || ''} — ${s.reason}`
    ));
    (plan.conflicts || []).forEach(c => push(
      c.store_id, 'conflict', 'conflict',
      c.message || `${c.item_code || ''} — η τιμή του efood άλλαξε από τότε που άνοιξες την οθόνη`
    ));
    return Object.keys(byStore).map(key => ({
      store_id: key,
      store_name: names[key] || (key ? `κατ. ${key}` : 'Χωρίς κατάστημα'),
      entries: byStore[key],
    }));
  }, [plan.skipped, plan.conflicts, plan.jobs]);

  const notSentTotal = notSent.reduce((sum, g) => sum + g.entries.length, 0);

  const jobs = plan.jobs || [];
  /**
   * ΠΡΙΝ τη μετάπτωση δεν υπάρχει τρόπος να απευθυνθούμε σε είδος του efood
   * (§13.18: οντότητες χωρίς δικό μας κωδικό δεν ενημερώνονται με ΚΑΝΕΝΑ id).
   * Άρα μια εργασία που δεν λέει `our_sku` δεν είναι «λίγο επικίνδυνη» — είναι
   * αδύνατη, και το κουμπί δεν επιτρέπεται να την προσφέρει.
   */
  const notOurSku = jobs.filter(j => j.addressing !== 'our_sku');
  const pushReady = canPush && serverCanPush && !expired && allAcked
    && jobs.length > 0 && notOurSku.length === 0 && !busy && !pushing && !pushProgress;
  const pushWhyNot = pushProgress
    ? 'Αυτό το σχέδιο έχει ήδη σταλεί — δες την πρόοδο παρακάτω. Νέα αποστολή θέλει νέα στεγνή δοκιμή.'
    : !canPush
    ? 'Η αποστολή γίνεται μόνο από διαχειριστή του οργανισμού.'
    : !serverCanPush
      ? 'Αυτός ο server δεν έχει άδεια εξερχόμενης εγγραφής.'
      : jobs.length === 0
        ? 'Δεν υπάρχει εργασία προς αποστολή.'
        : notOurSku.length > 0
          ? `${notOurSku.length} ${notOurSku.length === 1 ? 'κατάστημα δεν έχει' : 'καταστήματα δεν έχουν'} μεταπτωθεί`
            + ' — χωρίς δικούς μας κωδικούς δεν μπορούμε να απευθυνθούμε στα είδη τους.'
          : expired
            ? 'Η προεπισκόπηση έληξε — χρειάζεται νέα δοκιμή.'
            : !allAcked
              ? 'Λείπουν επιβεβαιώσεις φραγών.'
              : '';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-surface-2 border border-line-strong rounded-2xl w-full max-w-5xl my-6 shadow-2xl">
        {/* Κεφαλίδα */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-line">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Στεγνή δοκιμή — καμία εξερχόμενη κλήση
            </h3>
            <p className="text-xs text-t3 mt-1">
              {plan.run.stores} καταστήματα · {plan.run.edits} αλλαγές ·{' '}
              {plan.totals ? `${plan.totals.http_calls} κλήσεις HTTP, ${plan.totals.items_touched} είδη, ~${plan.totals.estimated_seconds}΄΄` : ''}
            </p>
            <p className="text-[11px] text-t4 mt-1 font-mono break-all">
              confirm_hash: {plan.run.confirm_hash}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {left !== null && (
              <span className={`text-xs px-2 py-1 rounded-lg ${
                expired
                  ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                  : 'bg-surface-3 text-t2'}`}
                title="Η προεπισκόπηση δένεται με το φρέσκο pre-image των 10΄ (§13.6.3)"
              >
                {expired ? 'Έληξε' : `Λήγει σε ${fmtCountdown(left)}`}
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-3 text-t3">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Η κεφαλίδα του συγχρονισμού τιμών: πόσα πρόχειρα φτιάχτηκαν ΜΟΛΙΣ
              τώρα και πόσα παραλείφθηκαν επίτηδες (§13.19.Β). Χωρίς αυτό, ο
              χειριστής βλέπει «43 αλλαγές» χωρίς να ξέρει από πού ήρθαν. */}
          {syncHeader && (
            <div className="border border-line rounded-xl p-3 bg-surface-1/50">
              <div className="flex items-center gap-2 text-sm font-medium text-t1">
                <Coins className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Συγχρονισμός τιμών από τον κατάλογο
              </div>
              <p className="text-xs text-t2 mt-1">
                Νέες πρόχειρες αλλαγές: <strong>{syncHeader.created}</strong> ·
                ενημερώθηκαν: <strong>{syncHeader.updated}</strong> ·
                σε <strong>{syncHeader.stores}</strong> καταστήματα.
              </p>
              <p className="text-[11px] text-t3 mt-0.5">
                Παραλείφθηκαν: {syncHeader.skipped_keep_platform} με απόφαση «κράτα την τιμή efood» ·{' '}
                {syncHeader.skipped_no_change} χωρίς διαφορά
                {syncHeader.skipped_conflict > 0 ? ` · ${syncHeader.skipped_conflict} σε σύγκρουση` : ''}.
                Η διαθεσιμότητα δεν άλλαξε πουθενά.
              </p>
            </div>
          )}

          {/* Blockers — κόκκινα, με ΥΠΟΧΡΕΩΤΙΚΟ checkbox το καθένα */}
          {blockers.length > 0 && (
            <div className="border border-red-300 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
                <ShieldAlert className="w-4 h-4" /> {blockers.length} φραγές — κάθε μία θέλει ρητή επιβεβαίωση
              </div>
              {blockers.map((b, i) => {
                const key = blockerKey(b);
                return (
                  <label key={`${key}-${i}`} className="flex items-start gap-2 text-xs text-red-900 dark:text-red-100">
                    <input
                      type="checkbox"
                      checked={!!ackBlockers[key]}
                      onChange={e => setAckBlockers({ ...ackBlockers, [key]: e.target.checked })}
                      className="w-4 h-4 rounded mt-0.5 shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="font-mono text-[10px] opacity-70">{b.code}</span>
                      {b.store_id ? <span className="opacity-70"> · κατ. {b.store_id}</span> : null}
                      {b.item_code ? <span className="opacity-70"> · {b.item_code}</span> : null}
                      {b.effect ? (
                        <span className="ml-1 px-1 rounded text-[10px] bg-red-500/20"
                          title="Τι κάνει η φραγή στο σχέδιο">
                          {b.effect === 'skip_store' ? 'πετά το κατάστημα'
                            : b.effect === 'skip_edit' ? 'πετά την αλλαγή'
                              : 'θέλει επιβεβαίωση'}
                        </span>
                      ) : null}
                      <br />
                      {b.message}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Προειδοποιήσεις — κίτρινες, δεν φράζουν */}
          {warnings.length > 0 && (
            <div className="border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4" /> Προειδοποιήσεις
              </div>
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-900 dark:text-amber-100">
                  {w.store_id ? <span className="opacity-70">κατ. {w.store_id}: </span> : null}{w.message}
                </p>
              ))}
            </div>
          )}

          {/* Δίδυμα τιμής — checkbox ΑΝΑ ΟΜΑΔΑ */}
          {plan.twins && plan.twins.length > 0 && (
            <div className="border border-line rounded-xl p-3 space-y-2">
              <div className="text-sm font-medium text-t1">Δίδυμα τιμής ({plan.twins.length} ομάδες)</div>
              {plan.twins.map((t, i) => (
                <label key={`${twinKeyOf(t)}-${i}`} className="flex items-start gap-2 text-xs text-t2">
                  <input
                    type="checkbox"
                    checked={!!twinToggles[twinKeyOf(t)]}
                    onChange={e => setTwinToggles({ ...twinToggles, [twinKeyOf(t)]: e.target.checked })}
                    className="w-4 h-4 rounded mt-0.5 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="text-t1">«{t.twin_key}»</span>
                    <span className="text-t3"> · κατ. {t.store_id} · {t.members} μέλη</span>
                    {!t.prices_agree && (
                      <span className="ml-1 px-1 rounded text-[10px] bg-amber-500/20 text-amber-800 dark:text-amber-200">
                        ήδη διαφορετικές τιμές
                      </span>
                    )}
                    <br />
                    {t.message}
                    {t.codes && t.codes.length > 0 && (
                      <span className="block text-[10px] text-t4 font-mono mt-0.5">{t.codes.join(' · ')}</span>
                    )}
                  </span>
                </label>
              ))}
              {twinsChanged && (
                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                  Άλλαξες ομάδα διδύμων: το σχέδιο (και το confirm_hash) πρέπει να ξαναϋπολογιστεί —
                  πάτα «Νέα δοκιμή» για να ξαναφτιαχτεί με αυτές τις αποφάσεις.
                </p>
              )}
              {twinDecisionsIgnored && (
                <p className="text-[11px] text-red-700 dark:text-red-300">
                  Ο server αγνόησε τις αποφάσεις διδύμων (δεν τις προωθεί ακόμη στον planner): το σχέδιο
                  παρακάτω δείχνει τις ΠΡΟΕΠΙΛΟΓΕΣ — δίδυμα με ίδια τιμή αλλάζουν μαζί, με διαφορετική όχι.
                </p>
              )}
            </div>
          )}

          {/* Ό,τι ΔΕΝ στέλνεται — ανά κατάστημα, με τον λόγο του planner */}
          {notSentTotal > 0 && (
            <div className="border border-line rounded-xl p-3 space-y-2">
              <div className="text-sm font-medium text-t1">
                Δεν στέλνονται ({notSentTotal} {notSentTotal === 1 ? 'αλλαγή' : 'αλλαγές'})
              </div>
              <p className="text-[11px] text-t4">
                Οι γραμμές αυτές μένουν πρόχειρες: κόπηκαν πριν γίνουν κλήση. Τα σύνολα της κεφαλίδας
                μετρούν τις αλλαγές του καλαθιού, όχι τις γραμμές που τελικά φεύγουν.
              </p>
              {notSent.map(group => (
                <div key={group.store_id || 'none'} className="text-xs">
                  <div className="text-t2 font-medium">{group.store_name}</div>
                  <ul className="mt-0.5 space-y-0.5">
                    {group.entries.map((e, i) => (
                      <li key={`${e.code}-${i}`} className="text-t3 flex items-start gap-1.5">
                        <span className={`px-1 rounded text-[10px] shrink-0 mt-0.5 ${
                          e.kind === 'conflict'
                            ? 'bg-red-500/20 text-red-700 dark:text-red-300'
                            : 'bg-surface-3 text-t3'}`}
                        >
                          {e.code}
                        </span>
                        <span className="min-w-0">{e.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Επιπτώσεις στη φάση 1 */}
          {plan.phase1_effects && plan.phase1_effects.length > 0 && (
            <div className="border border-line rounded-xl p-3 space-y-1">
              <div className="text-sm font-medium text-t1">Επιπτώσεις στις αντιστοιχίσεις ονομάτων</div>
              {plan.phase1_effects.map((e, i) => (
                <p key={i} className="text-xs text-t2">
                  <span className="font-mono text-[10px] text-t4">{e.code}</span> {e.message}
                  {e.vat !== undefined && e.vat !== null ? <strong> (ΦΠΑ {e.vat}%)</strong> : null}
                </p>
              ))}
            </div>
          )}

          {/* Οι εργασίες, μία ανά κατάστημα */}
          <div className="space-y-2">
            {(plan.jobs || []).map(job => (
              <JobAccordion
                key={job.store_id}
                job={job}
                open={!!openJobs[job.store_id]}
                onToggle={() => setOpenJobs({ ...openJobs, [job.store_id]: !openJobs[job.store_id] })}
              />
            ))}
            {(!plan.jobs || plan.jobs.length === 0) && (
              <p className="text-sm text-t4">Καμία εργασία: δεν υπάρχει αλλαγή που να μπορεί να σταλεί.</p>
            )}
          </div>
        </div>

        {/* Υποσέλιδο */}
        <div className="p-4 border-t border-line flex flex-wrap items-center gap-2">
          {/* 409/403 της πύλης: ΜΟΝΙΜΟ μήνυμα μέσα στο παράθυρο, δίπλα στο κουμπί
              που το προκάλεσε — ένα toast 2,5΄΄ θα χανόταν πριν διαβαστεί. */}
          {pushError && (
            <div className="w-full p-3 rounded-xl text-sm flex items-start gap-2 border bg-amber-50 dark:bg-amber-900/25 border-amber-300 dark:border-amber-700/50 text-amber-800 dark:text-amber-200">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="min-w-0">{pushError}</span>
            </div>
          )}

          {/* Η ΠΡΟΟΔΟΣ της εκτέλεσης (§11.9). Η αποστολή απαντά 202 και τρέχει
              στο παρασκήνιο — 20 καταστήματα είναι ~20΄ και κανένα αίτημα HTTP
              δεν ζει τόσο. Το παράθυρο ρωτά κάθε 3΄΄ και ΔΕΝ λέει ποτέ
              «Αποτυχία» επειδή δεν απαντήθηκε ένα ερώτημα. */}
          {pushProgress && (
            <div className="w-full p-3 rounded-xl border border-blue-300 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/20 space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-200">
                {pushProgress.finished
                  ? <Check className="w-4 h-4" />
                  : <Loader2 className="w-4 h-4 animate-spin" />}
                Εκτέλεση #{pushProgress.run_id} — {runStatusLabel(
                  pushProgress.detail ? pushProgress.detail.status : 'running'
                )}
                {pushProgress.detail && pushProgress.detail.halted_reason
                  ? ` (${pushProgress.detail.halted_reason})` : ''}
              </div>

              {canaryStoreName && (
                <p className="text-xs text-blue-900/80 dark:text-blue-200/80">
                  Καναρίνι (φεύγει πρώτο): <strong>{canaryStoreName}</strong>. Αν δεν επαληθευτεί καθαρά,
                  καμία άλλη εργασία δεν ξεκινά.
                </p>
              )}

              {pushProgress.detail && pushProgress.detail.jobs && pushProgress.detail.jobs.length > 0 && (
                <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                  {pushProgress.detail.jobs.map(j => (
                    <li key={`${j.seq}-${j.store_id}`} className="text-[11px] text-t2 flex items-center gap-2">
                      <span className="text-t4">#{j.seq}</span>
                      <span className="truncate">{j.store_name || `κατ. ${j.store_id}`}</span>
                      <span className="ml-auto px-1.5 rounded bg-surface-3 text-t2">{runStatusLabel(j.status)}</span>
                      {j.blocked_reason && <span className="text-amber-700 dark:text-amber-300">{j.blocked_reason}</span>}
                      {j.error && <span className="text-red-700 dark:text-red-300 truncate max-w-[220px]">{j.error}</span>}
                    </li>
                  ))}
                </ul>
              )}

              {pushProgress.unreachable && (
                <p className="text-xs text-amber-800 dark:text-amber-200">{pushProgress.unreachable}</p>
              )}

              {pushProgress.detail && pushProgress.detail.interrupted && (
                <p className="text-xs text-red-700 dark:text-red-300">
                  Η εκτέλεση δείχνει «εκτελείται» χωρίς καμία ενημέρωση για πάνω από 30΄: πιθανότατα
                  διακόπηκε η διεργασία του server. ΜΗΝ ξαναστείλεις — έλεγξε πρώτα τι πρόλαβε να γραφτεί.
                </p>
              )}

              <p className="text-[11px] text-t4">
                Η εκτέλεση συνεχίζει στον server ακόμη κι αν κλείσεις το παράθυρο ή την οθόνη· η πλήρης
                εικόνα είναι στο «Ιστορικό», όπου γίνεται και η ακύρωση.
              </p>
            </div>
          )}
          <span className="text-xs text-t3">
            {blockers.length > 0
              ? `${blockers.filter(b => !!ackBlockers[blockerKey(b)]).length}/${blockers.length} φραγές επιβεβαιωμένες`
              : 'Καμία φραγή'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => onRerun(twinDecisions)}
              disabled={busy || pushing}
              title="Ξαναϋπολογίζει το σχέδιο με τις αποφάσεις διδύμων αυτού του παραθύρου"
              className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm"
            >
              Νέα δοκιμή
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 bg-surface-2 hover:bg-surface-3 text-t1 border border-line-strong rounded-xl text-sm"
            >
              Κλείσιμο
            </button>
            {/*
              ΦΑΣΗ 5: πραγματική αποστολή. Το `disabled` ΕΞΑΡΤΑΤΑΙ πλέον από τα
              checkbox: οι φραγές είναι η μόνη ανθρώπινη πύλη πριν από εξερχόμενη
              εγγραφή που δεν αναιρείται αυτόματα.
            */}
            <span title={pushReady
              ? 'Στέλνει ΠΡΑΓΜΑΤΙΚΑ τις αλλαγές στο efood, σειριακά ανά κατάστημα, με το καναρίνι πρώτο'
              : pushWhyNot}>
              <button
                type="button"
                disabled={!pushReady || !onPush}
                onClick={() => { if (onPush) onPush(); }}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium flex items-center gap-2"
              >
                {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Αποστολή σε {plan.run.stores} {plan.run.stores === 1 ? 'κατάστημα' : 'καταστήματα'}
              </button>
            </span>
          </div>
          {!pushReady && pushWhyNot && (
            <p className="w-full text-[11px] text-t4">{pushWhyNot}</p>
          )}
          {pushReady && (
            <p className="w-full text-[11px] text-t4">
              Πρώτο φεύγει το <strong>καναρίνι</strong>· αν δεν επαληθευτεί καθαρά, καμία άλλη εργασία δεν
              ξεκινά. Ό,τι σταλεί ΔΕΝ αναιρείται αυτόματα — αναιρείται μόνο με νέα αποστολή των παλιών τιμών.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DryRunModal;
